import React, { useState } from "react";
import { motion } from "framer-motion";
import { FiMapPin, FiPhone, FiMail, FiClock, FiSend, FiCheck, FiAlertCircle } from "react-icons/fi";
import { FiFacebook, FiTwitter, FiInstagram, FiYoutube } from "react-icons/fi";
import { toast } from "react-toastify";
import { contactAPI } from "../services/api";
import { useSiteSettings } from "../context/SiteSettingsContext";

const initialForm = { name: "", phone: "", email: "", message: "" };

const SectionLabel = ({ text }) => (
  <div className="flex items-center gap-3 mb-4">
    <div className="w-8 h-1 rounded-full bg-[#2563EB]" />
    <span className="text-[#2563EB] text-xs font-bold uppercase tracking-[0.25em]">{text}</span>
  </div>
);

const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { settings } = useSiteSettings();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = "Name is required";
    if (!form.phone.trim()) newErrors.phone = "Phone number is required";
    if (!form.message.trim()) newErrors.message = "Message is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await contactAPI.submit({
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
      });
      setSubmitted(true);
      setForm(initialForm);
    } catch (error) {
      const msg = error.response?.data?.message || "Failed to send message. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = (name) =>
    `w-full px-5 py-4 bg-[#F0F4FF] border border-blue-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2563EB]/30 focus:border-[#2563EB] text-slate-900 font-medium placeholder-slate-400 text-sm transition-all ${errors[name] ? "ring-2 ring-red-400 border-red-300 bg-red-50" : ""}`;

  const ErrorMsg = ({ name }) =>
    errors[name] ? (
      <div className="flex items-center gap-1 mt-2 text-red-500 text-xs">
        <FiAlertCircle className="w-3 h-3" />
        {errors[name]}
      </div>
    ) : null;

  return (
    <div className="bg-white">
      {/* Hero */}
      <div className="bg-[#1e3a8a] py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: "radial-gradient(circle, #ffffff 1px, transparent 1px)", backgroundSize: "32px 32px" }} />
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#2563EB]" />
        <div className="relative max-w-7xl mx-auto px-8 sm:px-12 lg:px-16">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-1 rounded-full bg-sky-400" />
              <p className="text-sky-400 text-xs font-bold uppercase tracking-[0.25em]">Get in Touch</p>
            </div>
            <h1 className="text-4xl sm:text-6xl font-bold text-white leading-tight mb-6">
              Contact <span className="text-sky-300">Us</span>
            </h1>
            <p className="text-blue-200/70 text-lg max-w-xl leading-relaxed">
              We'd love to hear from you. Reach out to us with any questions.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-8 sm:px-12 lg:px-16 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Contact Form */}
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.7 }} viewport={{ once: true }}>
            <SectionLabel text="Send Us a Message" />
            <h2 className="text-3xl font-bold text-[#0F172A] mb-10">
              Drop a <span className="text-[#2563EB]">Line</span>
            </h2>

            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-[#F0F4FF] rounded-2xl p-10 text-center border border-blue-100"
              >
                <div className="w-16 h-16 bg-[#2563EB] rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-md">
                  <FiCheck className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-bold text-[#0F172A] text-xl mb-3">Message Sent!</h3>
                <p className="text-slate-500 text-sm mb-8 leading-relaxed">
                  Thank you for contacting us. We will get back to you within 24 hours.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="px-6 py-3 bg-[#2563EB] text-white text-xs font-bold rounded-xl uppercase tracking-widest hover:bg-[#1D4ED8] transition-colors"
                >
                  Send Another
                </button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <input name="name" value={form.name} onChange={handleChange} placeholder="Your Full Name *" className={inputClass("name")} />
                  <ErrorMsg name="name" />
                </div>
                <div>
                  <div className="relative">
                    <FiPhone className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <input name="phone" value={form.phone} onChange={handleChange} placeholder="Phone Number *" className={`${inputClass("phone")} pl-12`} />
                  </div>
                  <ErrorMsg name="phone" />
                </div>
                <div>
                  <div className="relative">
                    <FiMail className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="Email Address (optional)" className={`${inputClass("email")} pl-12`} />
                  </div>
                </div>
                <div>
                  <textarea name="message" value={form.message} onChange={handleChange} rows={5} placeholder="Write your message here... *" className={`${inputClass("message")} resize-none`} />
                  <ErrorMsg name="message" />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full flex items-center justify-center gap-3 py-4 bg-[#2563EB] text-white font-bold rounded-xl uppercase tracking-widest text-xs hover:bg-[#1D4ED8] transition-colors disabled:opacity-70 shadow-md"
                >
                  {submitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>Send Message <FiSend className="w-4 h-4" /></>
                  )}
                </button>
              </form>
            )}
          </motion.div>

          {/* Contact Info */}
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.7 }} viewport={{ once: true }}>
            <SectionLabel text="Contact Information" />
            <h2 className="text-3xl font-bold text-[#0F172A] mb-10">
              Here to <span className="text-[#2563EB]">Help</span>
            </h2>

            <div className="space-y-4">
              {[
                {
                  icon: FiMapPin,
                  title: "Our Address",
                  lines: [
                    settings?.schoolName || "Hillbert English Boarding School ",
                    [settings?.address?.street, settings?.address?.city || "Kathmandu"].filter(Boolean).join(", "),
                    [settings?.address?.state, settings?.address?.country || "Nepal", settings?.address?.zipCode].filter(Boolean).join(" "),
                  ].filter(Boolean),
                },
                {
                  icon: FiPhone,
                  title: "Phone Numbers",
                  lines: [settings?.phone?.primary, settings?.phone?.secondary].filter(Boolean),
                },
                {
                  icon: FiMail,
                  title: "Email Address",
                  lines: [settings?.email].filter(Boolean),
                },
                {
                  icon: FiClock,
                  title: "Office Hours",
                  lines: [
                    settings?.officeHours?.monday_friday,
                    settings?.officeHours?.saturday,
                    settings?.officeHours?.sunday ? `Sunday: ${settings.officeHours.sunday}` : null,
                  ].filter(Boolean),
                },
              ].map((item, idx) => (
                <div key={idx} className="flex gap-5 bg-[#F0F4FF] rounded-2xl p-5 border border-blue-100 hover:border-[#2563EB]/30 hover:shadow-sm transition-all group">
                  <div className="w-11 h-11 bg-[#2563EB] rounded-xl flex items-center justify-center shrink-0 shadow-sm group-hover:bg-[#1D4ED8] transition-colors">
                    <item.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#0F172A] text-sm mb-1">{item.title}</h3>
                    {item.lines.map((line, lIdx) => (
                      <p key={lIdx} className="text-slate-500 text-sm mb-0.5 last:mb-0">{line}</p>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Social Media */}
            <div className="mt-6 bg-[#1e3a8a] rounded-2xl p-6 flex items-center justify-between">
              <h3 className="font-bold text-white text-sm uppercase tracking-widest">Follow Us</h3>
              <div className="flex gap-2">
                {[
                  { icon: FiFacebook, label: "Facebook", href: settings?.socialMedia?.facebook },
                  { icon: FiTwitter, label: "Twitter", href: settings?.socialMedia?.twitter },
                  { icon: FiInstagram, label: "Instagram", href: settings?.socialMedia?.instagram },
                  { icon: FiYoutube, label: "YouTube", href: settings?.socialMedia?.youtube },
                ].map((social, idx) => (
                  <a
                    key={idx}
                    href={social.href || "#"}
                    aria-label={social.label}
                    target={social.href && social.href !== "#" ? "_blank" : undefined}
                    rel={social.href && social.href !== "#" ? "noopener noreferrer" : undefined}
                    className="w-10 h-10 bg-white/10 hover:bg-[#2563EB] rounded-xl flex items-center justify-center text-blue-200 hover:text-white transition-all"
                  >
                    <social.icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Map Placeholder */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="mt-20 lg:mt-28">
          <div className="w-full h-96 bg-[#F0F4FF] rounded-3xl flex items-center justify-center relative overflow-hidden border border-blue-100">
            <div className="absolute inset-0 opacity-30"
              style={{ backgroundImage: "radial-gradient(circle, #2563EB 1px, transparent 1px)", backgroundSize: "30px 30px" }} />
            <div className="text-center relative z-10 p-8 bg-white rounded-2xl max-w-md shadow-lg border border-blue-100">
              <div className="w-12 h-12 bg-[#2563EB] rounded-xl flex items-center justify-center mx-auto mb-4 shadow-sm">
                <FiMapPin className="w-6 h-6 text-white" />
              </div>
              <p className="font-bold text-[#0F172A] text-lg mb-2">
                {settings?.schoolName || "Hillbert English Boarding School "}
              </p>
              <p className="text-sm text-slate-500 mb-6">
                {[settings?.address?.city || "Kathmandu", settings?.address?.country || "Nepal"].filter(Boolean).join(", ")}
              </p>
              <a
                href="https://maps.google.com/?q=Baneshwor+Kathmandu+Nepal"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#2563EB] text-white text-xs font-bold rounded-xl uppercase tracking-widest hover:bg-[#1D4ED8] transition-colors shadow-sm"
              >
                <FiMapPin className="w-4 h-4" />
                Open in Maps
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Contact;
